import React from 'react'
import PropTypes from 'prop-types'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'

// Third Parties
import {useIntl, FormattedMessage} from 'react-intl'

// Custom Contexts
import {useConversionRate} from '../../custom-contexts'

// Custom hooks
import useShipToCountry from '../../hooks/use-ship-to-country'

// utils
import {formatPrice} from '../../utils/price-utils'

/**
 * PricePerItem is a React functional component that displays the price of an item
 * formatted as currency, followed by the word "each". This component is useful for
 * displaying the price per unit of an item in a list or catalog.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.currency - The currency code (e.g., 'USD', 'EUR') to format the price.
 * @param {number} props.basePrice - The base price of the item to be displayed.
 * @param {...Object} props - Additional properties that can be passed to the Text component.
 *
 * @returns {JSX.Element} A JSX element that renders the formatted price and the word "each".
 *
 * @example
 * <PricePerItem currency="USD" basePrice={10.99} />
 * // Renders: $10.99 each
 */
const PricePerItem = ({currency, basePrice, conversionsRate, shipToCountryCode, ...props}) => {
    const contextConversionsRate = useConversionRate()
    const {shipToCountry: contextShipToCountry} = useShipToCountry()
    const intl = useIntl()

    const priceConversionsRate = conversionsRate || contextConversionsRate
    const shipToCountryId = shipToCountryCode || contextShipToCountry?.id

    return (
        <Text variant="bodySmall" color="darkGray" {...props}>
            {formatPrice(basePrice, currency, priceConversionsRate, shipToCountryId, intl)}
            &nbsp;
            <FormattedMessage
                defaultMessage="each"
                id="price_per_item.label.each"
                description="'each', follows price per item, like $10/each"
            />
        </Text>
    )
}

PricePerItem.propTypes = {
    currency: PropTypes.string.isRequired,
    basePrice: PropTypes.number,
    conversionsRate: PropTypes.object,
    shipToCountryCode: PropTypes.string
}

export default PricePerItem
